// ============================================================
// ApproverNotes.tsx — (Settlement redesign) the notes for the approver as a short
// checklist of one-line rows, not paragraphs. A note with several lines (bill
// details) folds under its first line. A note that can be fixed offers the fix
// where it appears and goes away once fixed; the one tick stays beside the main
// button's step, as the server still requires.
//   • the risk-assessment note is saved right here: it changes no figures, so a
//     prepared or approved statement stays as it is (server: saveRiskNote);
//   • a fix that WOULD change figures says so before anything is saved, and on a
//     prepared statement waits for "Send back for changes".
// ============================================================
import React, { useId, useState } from 'react'
import type { FixTarget, StatementIssue } from '../../../types/settlementStatement.types'
import { B_SECONDARY, INPUT, NOTE } from '../../billing/billingUi'
import { ErrorNote } from '../../shared/ActionBox'
import FieldError from '../../shared/FieldError'
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard'

export const RISK_NOTE_CODE = 'RISK_ASSESSMENT_MISSING'
export const RISK_NOTE_KEEPS = 'This changes no figures, so the statement stays as it is.'
export const CHANGES_FIGURES = 'Fixing this changes the figures, so send the statement back for changes first.'
const LINK = 'text-[13px] font-semibold text-casevo-link whitespace-nowrap focus:outline-none casevo-focus rounded-[4px] hover:underline underline-offset-2'

// What each fixable note's fix is. Anything else is a plain row.
const FIX_OF: Record<string, { label: string; target: FixTarget; changesFigures: boolean }> = {
  ATE_COMMISSION_NOT_DISCLOSED: { label: 'Open ATE', target: 'ateCard', changesFigures: true },
  NO_LITIGATION_FRIEND: { label: 'Record them', target: 'litigationFriend', changesFigures: false },
}

const RiskNoteForm: React.FC<{ save: (note: string) => Promise<string | null>; onClose: () => void }> = ({ save, onClose }) => {
  const id = useId()
  const [note, setNote] = useState('')
  const [tried, setTried] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const guard = useUnsavedChangesGuard(note.trim() !== '' && !busy)
  const empty = !note.trim()
  const submit = async (): Promise<void> => {
    setTried(true)
    if (empty) return
    setBusy(true); setError(null)
    const refused = await save(note.trim())
    setBusy(false)
    if (refused) setError(refused); else onClose()
  }
  return (
    <div className="w-full flex flex-col gap-[8px] mt-[6px]" data-risk-note-form="">
      <label className="text-[12.5px] font-semibold text-casevo-forest" htmlFor={id}>Risk-assessment note</label>
      <textarea id={id} className={`${INPUT} h-auto min-h-[76px] py-2`} value={note} maxLength={5000} disabled={busy} onChange={(e) => setNote(e.target.value)}
        aria-invalid={(tried && empty) || undefined} aria-describedby={`${id}-keeps`} />
      {tried && <FieldError message={empty ? 'Write the risk-assessment note.' : undefined} />}
      <p id={`${id}-keeps`} className={`${NOTE} m-0`}>Saved on the CFA. {RISK_NOTE_KEEPS}</p>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex items-center gap-[8px] flex-wrap">
        <button type="button" className={B_SECONDARY} disabled={busy} onClick={() => { void submit() }}>{busy ? 'Saving…' : 'Save note'}</button>
        <button type="button" className={B_SECONDARY} disabled={busy} onClick={() => guard(onClose)}>Cancel</button>
      </div>
    </div>
  )
}

const NoteText: React.FC<{ message: string }> = ({ message }) => {
  const lines = message.split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length < 2) return <span className="min-w-0">{message}</span>
  return (
    <details className="min-w-0" data-note-lines="">
      <summary className="cursor-pointer focus:outline-none casevo-focus rounded-[4px]">{lines[0].replace(/:$/, '')}</summary>
      <ul className="m-0 mt-[4px] p-0 list-none flex flex-col gap-[2px] text-[12.5px] text-casevo-text-soft">{lines.slice(1).map((l) => <li key={l}>{l}</li>)}</ul>
    </details>
  )
}

const ApproverNotes: React.FC<{
  notes: StatementIssue[]
  locked: boolean
  busy: boolean
  onFix: (t: FixTarget) => void
  // Saves the risk note; a refusal comes back as its text (shown beside the form).
  saveRiskNote?: (note: string) => Promise<string | null>
  reviewedLine?: string | null
}> = ({ notes, locked, busy, onFix, saveRiskNote, reviewedLine }) => {
  const [adding, setAdding] = useState(false)
  return (
    <div role="group" aria-label="Notes for the approver">
      <h4 className="m-0 mb-[2px] text-[12px] font-semibold text-casevo-text-soft">Notes for the approver</h4>
      <ul className="m-0 p-0 list-none divide-y divide-casevo-border-soft text-[13.5px] text-casevo-forest">
        {notes.map((n) => {
          const fix = FIX_OF[n.code]
          const risk = n.code === RISK_NOTE_CODE && !!saveRiskNote
          return (
            <li key={n.code} data-note={n.code} className="py-[7px]">
              <div className="flex items-baseline justify-between gap-[12px]">
                <NoteText message={n.message} />
                {risk && !adding && <button type="button" className={LINK} disabled={busy} onClick={() => setAdding(true)}>Add note</button>}
                {fix && (!fix.changesFigures || !locked) && <button type="button" className={LINK} disabled={busy} onClick={() => onFix(fix.target)}>{fix.label}</button>}
              </div>
              {fix?.changesFigures && <p className={`${NOTE} m-0 mt-[2px]`}>{locked ? CHANGES_FIGURES : 'Fixing this changes the figures.'}</p>}
              {risk && adding && saveRiskNote && <RiskNoteForm save={saveRiskNote} onClose={() => setAdding(false)} />}
            </li>
          )
        })}
      </ul>
      {reviewedLine && <p className={`${NOTE} m-0 mt-[2px]`}>{reviewedLine}</p>}
    </div>
  )
}

export default ApproverNotes
